import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider as RadixToastProvider,
  ToastTitle,
  type ToastTone,
  ToastViewport,
} from "@/components/ui/toast";

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** One optional action (e.g. "Sign in", "Open"). */
  action?: { label: string; onClick: () => void };
  /** Duration in ms; default 6 s. */
  duration?: number;
}

interface ToastState extends ToastOptions {
  id: number;
  open: boolean;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
  dismiss: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const TOAST_DURATION_MS = 6_000;

/** One toast at a time (`docs/design/components.md`); a new one replaces the current. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ToastState | null>(null);

  const toast = useCallback((options: ToastOptions) => {
    setCurrent({ ...options, id: Date.now(), open: true });
  }, []);
  const dismiss = useCallback(() => {
    setCurrent((state) => (state ? { ...state, open: false } : state));
  }, []);
  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToastProvider swipeDirection="right" duration={TOAST_DURATION_MS}>
        {children}
        {current ? (
          <Toast
            key={current.id}
            tone={current.tone}
            open={current.open}
            duration={current.duration}
            onOpenChange={(open) => {
              if (!open) dismiss();
            }}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <ToastTitle>{current.title}</ToastTitle>
              {current.description ? (
                <ToastDescription>{current.description}</ToastDescription>
              ) : null}
            </div>
            {current.action ? (
              <ToastAction altText={current.action.label} onClick={current.action.onClick}>
                {current.action.label}
              </ToastAction>
            ) : null}
            <ToastClose />
          </Toast>
        ) : null}
        <ToastViewport label="Notifications" />
      </RadixToastProvider>
    </ToastContext.Provider>
  );
}

/** `toast({ title, description?, tone?, action? })` from anywhere under the provider. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
